import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { usePersonNameFormatter } from '@/lib/users/use-person-name'

import {
  activityDetailApi,
  type ActivityDetail,
  type Participant,
} from '../api'
import { feedbackAvailable } from '../access'
import { ConfirmDialog } from './confirm-dialog'
import type { ActivityNotice } from './feedback-notice'
import type { ParticipantsStatus } from './participants-panel'
import { UserAvatar } from './user-avatar'

interface FeedbackPanelProps {
  activity: ActivityDetail
  participants: Participant[]
  participantsStatus: ParticipantsStatus
  onChanged: () => void
  onNotice: (notice: ActivityNotice) => void
}

interface Scores {
  punctualityScore: number
  reliabilityScore: number
  respectScore: number
}

const DEFAULT_SCORES: Scores = {
  punctualityScore: 5,
  reliabilityScore: 5,
  respectScore: 5,
}

const SCORES = [1, 2, 3, 4, 5]

/**
 * Participant feedback (API_CONTRACT §6.14, D-043). Only confirmed participants
 * see the form, and only from one hour after `end_at`. The participant row's
 * `feedbackGiven` flag plus a local submitted set prevent a second form for the
 * same (activity, reviewer, reviewed) tuple.
 */
export function FeedbackPanel({
  activity,
  participants,
  participantsStatus,
  onChanged,
  onNotice,
}: FeedbackPanelProps) {
  const { t } = useTranslation()
  const formatPerson = usePersonNameFormatter()
  const headingId = useId()
  const punctualityId = useId()
  const reliabilityId = useId()
  const respectId = useId()
  const [target, setTarget] = useState<Participant | null>(null)
  const [scores, setScores] = useState<Scores>(DEFAULT_SCORES)
  const [submitted, setSubmitted] = useState<Set<string>>(() => new Set())
  const [markAbsent, setMarkAbsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!feedbackAvailable(activity)) return null
  if (participantsStatus !== 'ready') return null

  const others = participants.filter((participant) => !participant.mine)
  if (others.length === 0) return null

  const eligible = others.filter(
    (participant) => !participant.feedbackGiven && !submitted.has(participant.id),
  )

  function openFeedback(participant: Participant) {
    setScores(DEFAULT_SCORES)
    setMarkAbsent(false)
    setError(null)
    setTarget(participant)
  }

  async function submitFeedback() {
    if (!target || busy) return
    setBusy(true)
    setError(null)
    try {
      if (markAbsent && activity.viewerStatus === 'creator') {
        await activityDetailApi.setParticipantAttendance(
          activity.id,
          target.id,
          'absent',
        )
      }
      await activityDetailApi.submitFeedback(activity.id, {
        reviewedUserId: target.id,
        ...scores,
      })
      setSubmitted((current) => {
        const next = new Set(current)
        next.add(target.id)
        return next
      })
      setTarget(null)
      onNotice({ tone: 'success', key: 'activityDetail.feedback.success' })
      onChanged()
    } catch (error) {
      setError(friendlyErrorKey(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      aria-labelledby={headingId}
      className="space-y-3"
    >
      <h2
        id={headingId}
        className="font-head text-[18px] font-extrabold text-deep"
      >
        {t('activityDetail.feedback.title')}
      </h2>
      <p className="mt-1 text-sm leading-relaxed text-muted">
        {t('activityDetail.feedback.panelBody')}
      </p>

      {eligible.length === 0 ? (
        <p className="mt-3 text-sm font-semibold text-muted">
          {t('activityDetail.feedback.allGiven')}
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-line">
          {eligible.map((participant) => (
            <li key={participant.id}>
              <div className="flex items-center gap-3 py-3">
                <UserAvatar
                  name={participant.publicName}
                  src={participant.avatarUrl}
                />
                <p className="min-w-0 flex-1 text-sm font-bold break-words text-deep">
                  {formatPerson(participant)}
                </p>
                <button
                  type="button"
                  className="btn-secondary min-h-11 shrink-0 bg-white px-3 text-sm"
                  aria-label={t('activityDetail.feedback.prompt', {
                    name: formatPerson(participant),
                  })}
                  onClick={() => openFeedback(participant)}
                >
                  {t('activityDetail.feedback.open')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={target !== null}
        titleKey="activityDetail.feedback.prompt"
        titleValues={{ name: formatPerson(target) }}
        bodyKey="activityDetail.feedback.body"
        confirmKey={
          busy
            ? 'activityDetail.feedback.submitting'
            : 'activityDetail.feedback.submit'
        }
        cancelKey="activityDetail.feedback.cancel"
        busy={busy}
        onConfirm={() => void submitFeedback()}
        onCancel={() => {
          if (busy) return
          setTarget(null)
          setMarkAbsent(false)
          setError(null)
        }}
      >
        <div className="space-y-3">
          <div>
            <label htmlFor={punctualityId} className="field-label text-sm">
              {t('activityDetail.feedback.punctuality')}
            </label>
            <select
              id={punctualityId}
              className="field-input"
              value={scores.punctualityScore}
              onChange={(event) =>
                setScores((current) => ({
                  ...current,
                  punctualityScore: Number(event.target.value),
                }))
              }
            >
              {SCORES.map((score) => (
                <option key={score} value={score}>
                  {t('activityDetail.feedback.score', { score })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={reliabilityId} className="field-label text-sm">
              {t('activityDetail.feedback.reliability')}
            </label>
            <select
              id={reliabilityId}
              className="field-input"
              value={scores.reliabilityScore}
              onChange={(event) =>
                setScores((current) => ({
                  ...current,
                  reliabilityScore: Number(event.target.value),
                }))
              }
            >
              {SCORES.map((score) => (
                <option key={score} value={score}>
                  {t('activityDetail.feedback.score', { score })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={respectId} className="field-label text-sm">
              {t('activityDetail.feedback.respect')}
            </label>
            <select
              id={respectId}
              className="field-input"
              value={scores.respectScore}
              onChange={(event) =>
                setScores((current) => ({
                  ...current,
                  respectScore: Number(event.target.value),
                }))
              }
            >
              {SCORES.map((score) => (
                <option key={score} value={score}>
                  {t('activityDetail.feedback.score', { score })}
                </option>
              ))}
            </select>
          </div>
          {activity.viewerStatus === 'creator' ? (
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-0.5 size-5 shrink-0 accent-teal"
                checked={markAbsent}
                onChange={(event) => setMarkAbsent(event.target.checked)}
              />
              <span className="text-sm font-semibold text-deep">
                {t('activityDetail.feedback.absentCheckbox')}
              </span>
            </label>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm font-semibold text-danger">
              {t(error)}
            </p>
          ) : null}
        </div>
      </ConfirmDialog>
    </section>
  )
}
